import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { C as TOKENS, FONT } from "../constants";
import PageHeader from "../components/PageHeader";
import { getPlannerContext, plannerContextBanner } from "../utils/plannerContext";
import { getApiBase } from "../apiBase";
import { formatApiError } from "../utils/apiError";

const BASE = getApiBase();
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const LS_KEY = "qbank_draft";

// Align with Schedule Planner blue tokens
const C = {
  primary: TOKENS.primary,
  primaryLight: TOKENS.primarySoft,
  primary2: TOKENS.primary2,
  primaryDark: TOKENS.primaryDark,
  bg: TOKENS.bg,
  surface: TOKENS.surface,
  border: TOKENS.border,
  error: TOKENS.error,
  errorBg: TOKENS.errorBg,
  success: TOKENS.success,
  successBg: TOKENS.successBg,
  muted: TOKENS.muted,
  text: TOKENS.text,
  subtleText: TOKENS.muted,
  warn: TOKENS.warning,
  warnBg: TOKENS.warningBg,
  indigo: TOKENS.primary2,
  cyan: "#0891B2",
  amber: TOKENS.warning,
  panelBg: TOKENS.surfaceAlt,
};

const DIFF_COLORS = {
  easy: TOKENS.success,
  medium: TOKENS.warning,
  hard: TOKENS.error,
  mixed: TOKENS.primary,
};

const globalStyles = `
  * { box-sizing: border-box; }
  input, textarea, select { font-family: inherit; }
  input:focus, textarea:focus, select:focus { outline: none !important; border-color: ${TOKENS.primary} !important; box-shadow: 0 0 0 3px ${TOKENS.primary}20; }
  button:focus-visible { outline: 2px solid ${TOKENS.primary}; outline-offset: 2px; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: ${TOKENS.bg}; }
  ::-webkit-scrollbar-thumb { background: ${TOKENS.borderStrong}; border-radius: 3px; }
  ::-webkit-scrollbar-thumb:hover { background: ${TOKENS.primary2}; }
  @keyframes shimmer { 0%{transform:translateX(-200%)} 100%{transform:translateX(300%)} }
  @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin { to { transform: rotate(360deg); } }
  .qb-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px ${TOKENS.primary}40 !important; }
  .qb-btn-outline:hover { background: ${TOKENS.primarySoft} !important; }
  .qb-bank-card:hover { border-color: ${TOKENS.primary2} !important; box-shadow: 0 2px 10px ${TOKENS.primary}14; }
  .qb-collapsible-header:hover { background: ${TOKENS.primarySoft} !important; }
  .qb-option-row { transition: background 0.15s; }
`;

// ── Reusable atoms ─────────────────────────────────────────────────────────────
function Badge({ label, color }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 600,
      padding: "3px 9px", borderRadius: 99, border: `1px solid ${color}30`,
      color, background: color + "12", whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

function FieldLabel({ children, required }) {
  return (
    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.07em", marginBottom: 6, textTransform: "uppercase" }}>
      {children}{required && <span style={{ color: C.error, marginLeft: 2 }}>*</span>}
    </label>
  );
}

function TextInput({ value, onChange, placeholder, type = "text" }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{
      width: "100%", padding: "9px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8,
      fontSize: 13, color: C.text, background: C.surface,
      transition: "border-color 0.15s, box-shadow 0.15s",
    }} />
  );
}

function Divider() {
  return <div style={{ height: 1, background: C.border, margin: "20px 0" }} />;
}

// ── Syllabus Input ─────────────────────────────────────────────────────────────
function SyllabusInput({ value, onChange }) {
  const [mode, setMode] = useState("manual");
  const [fileName, setFileName] = useState("");
  const fileRef = useRef();

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = ev => onChange(ev.target.result);
    reader.readAsText(file);
  }

  return (
    <div>
      {/* Mode toggle */}
      <div style={{
        display: "flex", gap: 0, marginBottom: 10,
        border: `1.5px solid ${C.border}`, borderRadius: 8, overflow: "hidden", width: "fit-content",
      }}>
        {[["manual", "✏️ Type / Paste"], ["upload", "📄 Upload File"]].map(([m, label]) => (
          <button key={m} onClick={() => setMode(m)} style={{
            padding: "6px 14px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
            background: mode === m ? C.primary : "transparent",
            color: mode === m ? "#fff" : C.muted,
            transition: "all 0.15s",
          }}>{label}</button>
        ))}
      </div>

      {mode === "manual" ? (
        <textarea value={value} onChange={e => onChange(e.target.value)}
          placeholder="Paste syllabus content, unit topics, or any relevant context…"
          rows={4} style={{
            width: "100%", padding: "9px 12px", border: `1.5px solid ${C.border}`, borderRadius: 8,
            fontSize: 13, resize: "vertical", color: C.text, lineHeight: 1.6,
            background: C.surface, transition: "border-color 0.15s, box-shadow 0.15s",
          }} />
      ) : (
        <div>
          <div
            onClick={() => fileRef.current?.click()}
            onMouseEnter={e => (e.currentTarget.style.borderColor = C.primary)}
            onMouseLeave={e => (e.currentTarget.style.borderColor = C.border)}
            style={{
              border: `2px dashed ${C.border}`, borderRadius: 10, padding: "22px 16px",
              textAlign: "center", cursor: "pointer", background: C.bg,
              transition: "border-color 0.15s",
            }}
          >
            <div style={{ fontSize: 30, marginBottom: 8 }}>📄</div>
            {fileName ? (
              <div style={{ fontSize: 13, color: C.success, fontWeight: 600 }}>✓ {fileName}</div>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>Click to upload syllabus</div>
                <div style={{ fontSize: 11, color: C.muted }}>.txt, .md, .csv — plain text files</div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".txt,.md,.csv,.text" onChange={handleFile} style={{ display: "none" }} />
          {value && fileName && (
            <div style={{ marginTop: 8, padding: "8px 12px", background: C.successBg, borderRadius: 8, fontSize: 12, color: C.success, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>✓ {value.length.toLocaleString()} characters loaded from {fileName}</span>
              <button onClick={() => { onChange(""); setFileName(""); }} style={{
                background: "transparent", border: "none", color: C.error, fontSize: 11, cursor: "pointer", fontWeight: 600,
              }}>✕ Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Question number badge ──────────────────────────────────────────────────────
function QNum({ n, color }) {
  return (
    <span style={{
      minWidth: 28, height: 28, borderRadius: 7,
      background: color + "15", color, fontWeight: 800, fontSize: 12,
      display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>{n}</span>
  );
}

// ── MCQ Card ──────────────────────────────────────────────────────────────────
function MCQCard({ q, i, showAnswers }) {
  return (
    <div style={{
      background: C.surface, borderRadius: 10, padding: "14px 16px", marginBottom: 10,
      border: `1.5px solid ${C.border}`, transition: "box-shadow 0.15s",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
        <QNum n={i + 1} color={C.indigo} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.6 }}>{q.question}</span>
        <Badge label={q.difficulty} color={DIFF_COLORS[q.difficulty] || C.muted} />
      </div>
      <div style={{ paddingLeft: 38 }}>
        {(q.options || []).map((opt, j) => {
          const isCorrect = j === q.correct;
          return (
            <div key={j} className="qb-option-row" style={{
              padding: "7px 12px", borderRadius: 8, marginBottom: 4, fontSize: 13,
              display: "flex", alignItems: "center", gap: 10,
              background: showAnswers && isCorrect ? C.successBg : C.bg,
              color: showAnswers && isCorrect ? C.success : C.subtleText,
              fontWeight: showAnswers && isCorrect ? 700 : 400,
              border: `1px solid ${showAnswers && isCorrect ? C.success + "40" : "transparent"}`,
            }}>
              <span style={{
                minWidth: 24, height: 24, borderRadius: 5,
                background: showAnswers && isCorrect ? C.success : C.border,
                color: showAnswers && isCorrect ? "#fff" : C.muted,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>{["A", "B", "C", "D"][j]}</span>
              <span style={{ flex: 1 }}>{opt}</span>
              {showAnswers && isCorrect && <span style={{ color: C.success, fontWeight: 700, fontSize: 14 }}>✓</span>}
            </div>
          );
        })}
        {showAnswers && q.explanation && (
          <div style={{
            marginTop: 10, fontSize: 12, color: C.subtleText, lineHeight: 1.7,
            padding: "10px 12px", background: C.warnBg, borderRadius: 8,
            borderLeft: `3px solid ${C.warn}`,
          }}>
            💡 <strong style={{ color: C.warn }}>Explanation:</strong> {q.explanation}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Short Answer Card ──────────────────────────────────────────────────────────
function SACard({ q, i, showAnswers }) {
  return (
    <div style={{
      background: C.surface, borderRadius: 10, padding: "14px 16px", marginBottom: 10,
      border: `1.5px solid ${C.border}`,
    }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: showAnswers ? 12 : 0 }}>
        <QNum n={i + 1} color={C.cyan} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.6 }}>{q.question}</span>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <Badge label={q.difficulty} color={DIFF_COLORS[q.difficulty] || C.muted} />
          <Badge label={`${q.marks}M`} color={C.indigo} />
        </div>
      </div>
      {showAnswers && (
        <div style={{ paddingLeft: 38, animation: "fadeIn 0.15s ease both" }}>
          <div style={{ background: C.panelBg, borderRadius: 9, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", marginBottom: 6 }}>MODEL ANSWER</div>
            <div style={{ fontSize: 13, color: C.subtleText, lineHeight: 1.7, whiteSpace: "pre-wrap", marginBottom: q.keyPoints?.length ? 10 : 0 }}>{q.modelAnswer}</div>
            {q.keyPoints?.length > 0 && (
              <div style={{ background: C.primaryLight, borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.primary, marginBottom: 6 }}>KEY POINTS</div>
                {q.keyPoints.map((kp, ki) => (
                  <div key={ki} style={{ fontSize: 12, color: C.subtleText, padding: "3px 0", display: "flex", gap: 6 }}>
                    <span style={{ color: C.primary, flexShrink: 0 }}>▸</span> {kp}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Long Answer Card ───────────────────────────────────────────────────────────
function LACard({ q, i, showAnswers }) {
  return (
    <div style={{
      background: C.surface, borderRadius: 10, padding: "14px 16px", marginBottom: 10,
      border: `1.5px solid ${C.border}`,
    }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap", marginBottom: showAnswers ? 12 : 0 }}>
        <QNum n={i + 1} color={C.amber} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.6 }}>{q.question}</span>
        <div style={{ display: "flex", gap: 4, flexShrink: 0, flexWrap: "wrap" }}>
          <Badge label={q.difficulty} color={DIFF_COLORS[q.difficulty] || C.muted} />
          <Badge label={`${q.marks}M`} color={C.indigo} />
          {q.suggestedTime && <Badge label={`~${q.suggestedTime}min`} color={C.cyan} />}
        </div>
      </div>
      {showAnswers && (
        <div style={{ paddingLeft: 38, animation: "fadeIn 0.15s ease both" }}>
          <div style={{ background: C.panelBg, borderRadius: 9, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", marginBottom: 6 }}>MODEL ANSWER</div>
            <div style={{ fontSize: 13, color: C.subtleText, lineHeight: 1.7, whiteSpace: "pre-wrap", marginBottom: q.keyPoints?.length ? 10 : 0 }}>{q.modelAnswer}</div>
            {q.keyPoints?.length > 0 && (
              <div style={{ background: "#E7F0F6", borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, marginBottom: 6 }}>KEY POINTS</div>
                {q.keyPoints.map((kp, ki) => (
                  <div key={ki} style={{ fontSize: 12, color: C.subtleText, padding: "3px 0", display: "flex", gap: 6 }}>
                    <span style={{ color: C.amber, flexShrink: 0 }}>▸</span> {kp}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Collapsible section ────────────────────────────────────────────────────────
function Collapsible({ title, count, children, accent = C.primary }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ border: `1.5px solid ${C.border}`, borderRadius: 12, marginBottom: 14, overflow: "hidden" }}>
      <div className="qb-collapsible-header" onClick={() => setOpen(o => !o)} style={{
        padding: "13px 18px", cursor: "pointer", display: "flex",
        justifyContent: "space-between", alignItems: "center",
        background: open ? accent + "08" : C.bg,
        borderBottom: open ? `1.5px solid ${C.border}` : "none",
        userSelect: "none", transition: "background 0.15s",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: open ? accent : C.text }}>{title}</span>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
            background: accent + "18", color: accent,
          }}>{count}</span>
        </span>
        <span style={{ fontSize: 12, color: C.muted, opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ padding: 16, background: C.surface, animation: "fadeIn 0.15s ease both" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Stats bar ─────────────────────────────────────────────────────────────────
function StatsBar({ bank, showAnswers, setShowAnswers, onDownload, totalQ }) {
  return (
    <div style={{
      background: C.surface, borderRadius: 12, padding: "16px 20px", marginBottom: 18,
      border: `1.5px solid ${C.border}`, display: "flex",
      justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
      boxShadow: "0 1px 4px #0001",
    }}>
      <div>
        <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{bank.subject}</div>
        <div style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>Topic: {bank.topic}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ textAlign: "center", padding: "0 14px", borderRight: `1px solid ${C.border}` }}>
          <div style={{ fontWeight: 900, fontSize: 26, color: C.primary, lineHeight: 1 }}>{totalQ}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Total Qs</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="qb-btn-outline" onClick={() => setShowAnswers(s => !s)} style={{
            padding: "8px 14px", borderRadius: 8,
            border: `1.5px solid ${showAnswers ? C.success : C.border}`,
            background: showAnswers ? C.successBg : C.surface,
            color: showAnswers ? C.success : C.muted,
            fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.15s",
          }}>{showAnswers ? "🙈 Hide Answers" : "👁 Show Answers"}</button>
          <button className="qb-btn-outline" onClick={onDownload} style={{
            padding: "8px 14px", borderRadius: 8, border: `1.5px solid ${C.border}`,
            background: C.surface, color: C.primary, fontSize: 12, fontWeight: 700,
            cursor: "pointer", transition: "all 0.15s",
          }}>⬇ Download</button>
        </div>
      </div>
    </div>
  );
}

// ── Counter widget ─────────────────────────────────────────────────────────────
function Counter({ label, value, onChange, max, color = C.primary }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
        <button onClick={() => onChange(Math.max(1, value - 1))} style={{
          width: 30, height: 30, borderRadius: 7, border: `1.5px solid ${C.border}`,
          background: C.surface, fontSize: 16, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", color: C.muted,
          transition: "all 0.15s",
        }}>−</button>
        <span style={{ fontWeight: 800, fontSize: 20, minWidth: 28, textAlign: "center", color: C.text }}>{value}</span>
        <button onClick={() => onChange(Math.min(max, value + 1))} style={{
          width: 30, height: 30, borderRadius: 7, border: `1.5px solid ${C.border}`,
          background: C.surface, fontSize: 16, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", color: C.muted,
          transition: "all 0.15s",
        }}>+</button>
      </div>
      <div style={{ fontSize: 10, color: color, marginTop: 4, fontWeight: 600 }}>{label === "MCQ" ? "≤30" : label === "SHORT" ? "≤20" : "≤10"}</div>
    </div>
  );
}

// ── Download helper ────────────────────────────────────────────────────────────
function downloadBank(bank) {
  const lines = [];
  lines.push(`QUESTION BANK — ${bank.subject} | ${bank.topic}`);
  lines.push("=".repeat(60));
  lines.push(`Generated: ${new Date(bank.updatedAt).toLocaleString()}\n`);
  lines.push("━━━ SECTION A: MCQ ━━━");
  (bank.mcq || []).forEach((q, i) => {
    lines.push(`\nQ${i + 1}. [${q.difficulty?.toUpperCase()}] ${q.question}`);
    (q.options || []).forEach((o, j) => lines.push(`  ${["A","B","C","D"][j]}. ${o}`));
    lines.push(`  Answer: ${["A","B","C","D"][q.correct]}   Explanation: ${q.explanation || ""}`);
  });
  lines.push("\n\n━━━ SECTION B: SHORT ANSWER ━━━");
  (bank.shortAnswer || []).forEach((q, i) => {
    lines.push(`\nQ${i + 1}. [${q.difficulty?.toUpperCase()} | ${q.marks}M] ${q.question}`);
    lines.push(`  Model Answer: ${q.modelAnswer}`);
    if (q.keyPoints?.length) lines.push(`  Key Points: ${q.keyPoints.join(" | ")}`);
  });
  lines.push("\n\n━━━ SECTION C: LONG ANSWER ━━━");
  (bank.longAnswer || []).forEach((q, i) => {
    lines.push(`\nQ${i + 1}. [${q.difficulty?.toUpperCase()} | ${q.marks}M | ~${q.suggestedTime}min] ${q.question}`);
    lines.push(`  Model Answer:\n${q.modelAnswer}`);
    if (q.keyPoints?.length) lines.push(`  Key Points: ${q.keyPoints.join(" | ")}`);
  });
  const blob = new Blob([lines.join("\n")], { type: "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `QuestionBank_${bank.subject}_${bank.topic}.txt`.replace(/\s+/g, "_");
  a.click();
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function QuestionBankGenerator() {
  const [tab, setTab]               = useState("generate");
  const [subject, setSubject]       = useState("");
  const [topic, setTopic]           = useState("");
  const [syllabusText, setSyllabus] = useState("");
  const [manualInst, setManualInst] = useState("");
  const [mcqCount, setMcqCount]     = useState(10);
  const [shortCount, setShortCount] = useState(5);
  const [longCount, setLongCount]   = useState(3);
  const [difficulty, setDifficulty] = useState("mixed");
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState("");
  const [showAnswers, setShowAnswers] = useState(false);
  const [saved, setSaved]           = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.subject) setSubject(d.subject);
        if (d.topic) setTopic(d.topic);
        if (d.syllabusText) setSyllabus(d.syllabusText);
        if (d.manualInst) setManualInst(d.manualInst);
        if (d.mcqCount) setMcqCount(d.mcqCount);
        if (d.shortCount) setShortCount(d.shortCount);
        if (d.longCount) setLongCount(d.longCount);
        if (d.difficulty) setDifficulty(d.difficulty);
      }
    } catch {}

    // Prefill empty fields from Schedule Planner first inputs
    const ctx = getPlannerContext();
    setSubject((s) => s || ctx.subject || "");
    setTopic((t) => t || ctx.topicHint || "");
    setSyllabus((sy) => {
      if (sy) return sy;
      return [ctx.syllabus, ctx.dsaSyllabus, ctx.questions, ctx.problem ? `Project context:\n${ctx.problem.slice(0, 1200)}` : ""]
        .filter(Boolean)
        .join("\n\n");
    });
  }, []);

  function saveDraft() {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ subject, topic, syllabusText, manualInst, mcqCount, shortCount, longCount, difficulty }));
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2000);
    } catch {}
  }

  function clearDraft() {
    localStorage.removeItem(LS_KEY);
    setSubject(""); setTopic(""); setSyllabus(""); setManualInst("");
    setMcqCount(10); setShortCount(5); setLongCount(3); setDifficulty("mixed");
  }

  useEffect(() => {
    if (tab === "saved") fetchSaved();
  }, [tab]);

  async function fetchSaved() {
    setSavedLoading(true);
    try {
      const r = await axios.get(`${BASE}/question-bank`, { headers: authH() });
      setSaved(r.data.data || []);
    } catch { setSaved([]); }
    setSavedLoading(false);
  }

  async function handleGenerate() {
    if (!subject.trim() || !topic.trim()) { setError("Subject and topic are required."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await axios.post(`${BASE}/question-bank/generate`, {
        subject: subject.trim(), topic: topic.trim(), syllabusText, manualInstructions: manualInst,
        counts: { mcq: mcqCount, short: shortCount, long: longCount }, difficulty,
      }, { headers: authH() });
      setResult(r.data.data);
      setShowAnswers(false);
    } catch (e) {
      setError(formatApiError(e, "Request failed"));
    }
    setLoading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this question bank?")) return;
    try {
      await axios.delete(`${BASE}/question-bank/${id}`, { headers: authH() });
      setSaved(s => s.filter(b => b._id !== id));
      if (selectedBank?._id === id) setSelectedBank(null);
    } catch {}
  }

  const totalQ = (result?.mcq?.length || 0) + (result?.shortAnswer?.length || 0) + (result?.longAnswer?.length || 0);

  return (
    <>
      <style>{globalStyles}</style>
      <div style={{
        maxWidth: 1060, margin: "0 auto", padding: "32px 20px 60px",
        fontFamily: FONT.family, background: C.bg, minHeight: "100vh",
      }}>

        {/* ── Page Header ── */}
        <PageHeader
          title="Question Bank"
          subtitle="AI-generated MCQ, short answer & long answer questions — upload your syllabus or type it in"
          icon="📚"
          style={{ marginBottom: 22 }}
        />

        <div style={{
          marginBottom: 14, padding: "10px 14px", borderRadius: 10,
          background: C.primaryLight, border: `1px solid ${C.primary}22`,
          fontSize: 12.5, color: C.primaryDark, fontWeight: 600,
        }}>
          {plannerContextBanner(getPlannerContext())}
        </div>

        {/* ── Tab Bar ── */}
        <div style={{
          display: "flex", gap: 2, marginBottom: 24, background: C.surface,
          padding: 4, borderRadius: 10, border: `1px solid ${C.border}`,
          width: "fit-content", boxShadow: "0 1px 3px #0001",
        }}>
          {[["generate", "⚡ Generate"], ["saved", "🗂 Saved Banks"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: "8px 22px", borderRadius: 7, border: "none", cursor: "pointer",
              fontWeight: 700, fontSize: 13,
              background: tab === key ? C.primary : "transparent",
              color: tab === key ? "#fff" : C.muted,
              transition: "all 0.15s",
              boxShadow: "none",
            }}>{label}</button>
          ))}
        </div>

        {/* ── GENERATE TAB ── */}
      {/* ── GENERATE TAB ── */}
{tab === "generate" && (
  <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

    {/* ── Top Form Panel (full width, no sticky/scroll) ── */}
    <div style={{
      background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`,
      boxShadow: "none",
    }}>
      {/* Panel header */}
      <div style={{
        padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        borderRadius: "16px 16px 0 0",
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Configure</span>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="qb-btn-outline" onClick={saveDraft} style={{
            padding: "5px 12px", borderRadius: 7,
            border: `1.5px solid ${draftSaved ? C.success : C.border}`,
            background: draftSaved ? C.successBg : "transparent",
            color: draftSaved ? C.success : C.muted,
            fontSize: 11, cursor: "pointer", fontWeight: 600, transition: "all 0.15s",
          }}>{draftSaved ? "✓ Saved!" : "💾 Draft"}</button>
          <button className="qb-btn-outline" onClick={clearDraft} style={{
            padding: "5px 10px", borderRadius: 7, border: `1.5px solid ${C.border}`,
            background: "transparent", color: C.muted, fontSize: 11, cursor: "pointer",
            transition: "all 0.15s",
          }}>↺ Clear</button>
        </div>
      </div>

      <div style={{ padding: "24px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 24 }}>

        {/* Column 1: Subject, Topic, Syllabus */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <FieldLabel required>Subject</FieldLabel>
            <TextInput value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. Data Structures" />
          </div>
          <div>
            <FieldLabel required>Topic</FieldLabel>
            <TextInput value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Binary Search Trees" />
          </div>
          <div>
            <FieldLabel>Syllabus / Context</FieldLabel>
            <SyllabusInput value={syllabusText} onChange={setSyllabus} />
          </div>
        </div>

        {/* Column 2: Teacher Instructions, Question Counts */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <FieldLabel>Teacher Instructions <span style={{ fontWeight: 400, textTransform: "none", fontSize: 10 }}>(optional)</span></FieldLabel>
            <textarea value={manualInst} onChange={e => setManualInst(e.target.value)}
              placeholder="e.g. Focus on application-level questions, add case studies, avoid memory-only MCQs…"
              rows={4} style={{
                width: "100%", padding: "9px 12px", border: `1.5px solid ${C.border}`,
                borderRadius: 8, fontSize: 13, resize: "vertical", color: C.text, lineHeight: 1.6,
                background: C.surface, transition: "border-color 0.15s, box-shadow 0.15s",
              }} />
          </div>
          <div>
            <FieldLabel>Question Counts</FieldLabel>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8,
              background: C.bg, borderRadius: 10, padding: "16px 12px", border: `1.5px solid ${C.border}`,
            }}>
              <Counter label="MCQ" value={mcqCount} onChange={setMcqCount} max={30} color={C.indigo} />
              <Counter label="SHORT" value={shortCount} onChange={setShortCount} max={20} color={C.cyan} />
              <Counter label="LONG" value={longCount} onChange={setLongCount} max={10} color={C.amber} />
            </div>
          </div>
        </div>

        {/* Column 3: Difficulty, Error, Generate */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <FieldLabel>Difficulty</FieldLabel>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[["mixed", "🎲 Mixed"], ["easy", "🟢 Easy"], ["medium", "🟡 Medium"], ["hard", "🔴 Hard"]].map(([d, label]) => (
                <button key={d} onClick={() => setDifficulty(d)} style={{
                  padding: "7px 14px", borderRadius: 99, cursor: "pointer", fontSize: 12, fontWeight: 600,
                  border: `1.5px solid ${difficulty === d ? DIFF_COLORS[d] : C.border}`,
                  background: difficulty === d ? DIFF_COLORS[d] + "15" : "transparent",
                  color: difficulty === d ? DIFF_COLORS[d] : C.muted,
                  transition: "all 0.15s",
                }}>{label}</button>
              ))}
            </div>
          </div>

          {error && (
            <div style={{
              background: C.errorBg, color: C.error, padding: "10px 14px",
              borderRadius: 9, fontSize: 13,
              borderLeft: `3px solid ${C.error}`, display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <span>⚠</span><span>{error}</span>
            </div>
          )}

          <button className="qb-btn-primary" onClick={handleGenerate} disabled={loading} style={{
            width: "100%", padding: "13px 0",
            background: loading ? C.muted : `linear-gradient(135deg, ${C.primary}, ${C.primary2})`,
            color: "#fff", border: "none", borderRadius: 10, fontWeight: 800, fontSize: 14,
            cursor: loading ? "not-allowed" : "pointer", letterSpacing: "0.02em",
            boxShadow: loading ? "none" : `0 4px 14px ${C.primary}30`,
            transition: "all 0.2s", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            {loading
              ? <><span style={{ width: 16, height: 16, border: "2px solid #ffffff60", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />Generating…</>
              : `⚡ Generate Question Bank`
            }
          </button>
        </div>
      </div>
    </div>

    {/* ── Results below ── */}
    <div>
      {!result && !loading && (
        <div style={{
          textAlign: "center", padding: "80px 20px", color: C.muted,
          background: C.surface, borderRadius: 16, border: `2px dashed ${C.border}`,
        }}>
          <div style={{ fontSize: 56, marginBottom: 14 }}>📝</div>
          <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 6, color: C.text }}>No questions yet</div>
          <div style={{ fontSize: 14, maxWidth: 260, margin: "0 auto", lineHeight: 1.6 }}>
            Fill in the subject & topic, then click Generate
          </div>
        </div>
      )}

      {loading && (
        <div style={{
          textAlign: "center", padding: "72px 20px",
          background: C.surface, borderRadius: 16, border: `1.5px solid ${C.border}`,
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: "50%",
            border: `3px solid ${C.border}`, borderTopColor: C.primary,
            margin: "0 auto 20px", animation: "spin 0.8s linear infinite",
          }} />
          <div style={{ fontWeight: 800, fontSize: 16, color: C.text, marginBottom: 6 }}>Generating questions…</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
            Crafting {mcqCount + shortCount + longCount} questions · Usually 15–30 seconds
          </div>
          <div style={{ maxWidth: 280, margin: "0 auto", height: 4, background: C.border, borderRadius: 99, overflow: "hidden" }}>
            <div style={{
              height: "100%", width: "50%",
              background: `linear-gradient(90deg, ${C.primary}, ${C.primary2})`,
              borderRadius: 99, animation: "shimmer 1.6s ease-in-out infinite",
            }} />
          </div>
        </div>
      )}

      {result && (
        <div style={{ animation: "fadeIn 0.2s ease both" }}>
          <StatsBar
            bank={result}
            showAnswers={showAnswers}
            setShowAnswers={setShowAnswers}
            onDownload={() => downloadBank(result)}
            totalQ={totalQ}
          />
          <Collapsible title="Section A — Multiple Choice" count={result.mcq?.length || 0} accent={C.indigo}>
            {(result.mcq || []).map((q, i) => <MCQCard key={i} q={q} i={i} showAnswers={showAnswers} />)}
          </Collapsible>
          <Collapsible title="Section B — Short Answer" count={result.shortAnswer?.length || 0} accent={C.cyan}>
            {(result.shortAnswer || []).map((q, i) => <SACard key={i} q={q} i={i} showAnswers={showAnswers} />)}
          </Collapsible>
          <Collapsible title="Section C — Long Answer" count={result.longAnswer?.length || 0} accent={C.amber}>
            {(result.longAnswer || []).map((q, i) => <LACard key={i} q={q} i={i} showAnswers={showAnswers} />)}
          </Collapsible>
        </div>
      )}
    </div>
  </div>
)}

        {/* ── SAVED TAB ── */}
       {tab === "saved" && (
  <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

    {/* ── Loading ── */}
    {savedLoading && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 48, color: C.muted }}>
        <div style={{ width: 20, height: 20, border: `2px solid ${C.border}`, borderTopColor: C.primary, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        Loading banks…
      </div>
    )}

    {/* ── Empty ── */}
    {!savedLoading && !saved.length && (
      <div style={{
        textAlign: "center", padding: 64, color: C.muted,
        background: C.surface, borderRadius: 14, border: `2px dashed ${C.border}`,
      }}>
        <div style={{ fontSize: 44, marginBottom: 10 }}>📭</div>
        <div style={{ fontWeight: 700, fontSize: 15 }}>No saved banks yet</div>
        <div style={{ fontSize: 13, marginTop: 6 }}>Generate questions and they'll appear here</div>
      </div>
    )}

    {/* ── Bank cards grid ── */}
    {!savedLoading && saved.length > 0 && (
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 14,
      }}>
        {saved.map(b => (
          <div
            key={b._id}
            className="qb-bank-card"
            onClick={() => setSelectedBank(selectedBank?._id === b._id ? null : b)}
            style={{
              background: selectedBank?._id === b._id ? C.primaryLight : C.surface,
              border: `1.5px solid ${selectedBank?._id === b._id ? C.primary : C.border}`,
              borderRadius: 12, padding: "16px 18px", cursor: "pointer",
              transition: "all 0.15s", boxShadow: "0 1px 3px #0001",
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 2 }}>{b.subject}</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>{b.topic}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
              <Badge label={`${b.mcq?.length || 0} MCQ`} color={C.indigo} />
              <Badge label={`${b.shortAnswer?.length || 0} Short`} color={C.cyan} />
              <Badge label={`${b.longAnswer?.length || 0} Long`} color={C.amber} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: C.muted }}>{new Date(b.updatedAt).toLocaleDateString()}</span>
              <button onClick={e => { e.stopPropagation(); handleDelete(b._id); }} style={{
                padding: "4px 12px", borderRadius: 6, border: `1.5px solid ${C.error}30`,
                background: "#FEF2F2", color: C.error, fontSize: 11, cursor: "pointer", fontWeight: 600,
              }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    )}

    {/* ── Selected bank viewer (full width below cards) ── */}
    {selectedBank && (
      <div style={{ animation: "fadeIn 0.2s ease both" }}>

        {/* Header with close button */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
            Viewing: <span style={{ color: C.primary }}>{selectedBank.subject} — {selectedBank.topic}</span>
          </div>
          <button onClick={() => setSelectedBank(null)} style={{
            padding: "5px 14px", borderRadius: 7, border: `1.5px solid ${C.border}`,
            background: C.surface, color: C.muted, fontSize: 12, fontWeight: 600,
            cursor: "pointer", transition: "all 0.15s",
          }}>✕ Close</button>
        </div>

        <StatsBar
          bank={selectedBank}
          showAnswers={showAnswers}
          setShowAnswers={setShowAnswers}
          onDownload={() => downloadBank(selectedBank)}
          totalQ={(selectedBank.mcq?.length || 0) + (selectedBank.shortAnswer?.length || 0) + (selectedBank.longAnswer?.length || 0)}
        />
        <Collapsible title="Section A — MCQ" count={selectedBank.mcq?.length || 0} accent={C.indigo}>
          {(selectedBank.mcq || []).map((q, i) => <MCQCard key={i} q={q} i={i} showAnswers={showAnswers} />)}
        </Collapsible>
        <Collapsible title="Section B — Short Answer" count={selectedBank.shortAnswer?.length || 0} accent={C.cyan}>
          {(selectedBank.shortAnswer || []).map((q, i) => <SACard key={i} q={q} i={i} showAnswers={showAnswers} />)}
        </Collapsible>
        <Collapsible title="Section C — Long Answer" count={selectedBank.longAnswer?.length || 0} accent={C.amber}>
          {(selectedBank.longAnswer || []).map((q, i) => <LACard key={i} q={q} i={i} showAnswers={showAnswers} />)}
        </Collapsible>
      </div>
    )}
  </div>
)}
      </div>
    </>
  );
}